import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { OwlMascot } from '../components/OwlMascot';
import axios from 'axios';
import { Clock, Users, BookOpen, ArrowRight, Sparkles, MessageSquare, Award, Library, GraduationCap, Briefcase, Star, ChevronRight, Check, ShieldCheck, Layers, BrainCircuit, BadgeCheck, Target, ChevronLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import SEO from '../components/SEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LandingPage() {
  const { language, t } = useLanguage();
  const navigate = useNavigate();
  const [programs, setPrograms] = useState([]);
  const [currentTestimonial, setCurrentTestimonial] = useState(0);

  useEffect(() => {
    const fetchPrograms = async () => {
      try {
        const res = await axios.get(`${API}/programs`);
        setPrograms(res.data);
      } catch (err) {
        console.error('Failed to fetch programs:', err);
      }
    };
    fetchPrograms();
    // Seed data only once per session
    if (!sessionStorage.getItem('seeded')) {
      axios.post(`${API}/seed`).then(() => sessionStorage.setItem('seeded', '1')).catch(() => {});
    }
  }, []);

  // Testimonial auto-rotation
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTestimonial(prev => (prev + 1) % 4);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  const organizationJsonLd = {
    "@context": "https://schema.org",
    "@type": "EducationalOrganization",
    "name": "IA Academy",
    "description": language === 'fr'
      ? "La formation IA la plus complète en français et en anglais. Du débutant au leader."
      : "The most comprehensive AI training in French and English. From beginner to leader.",
    "url": "https://ia-academy.ai",
    "logo": "https://customer-assets.emergentagent.com/job_2071b760-1de1-4ab9-81d8-8ca2ec59ffcc/artifacts/lq8u7cy5_LOGO%20IA.png",
    "sameAs": [],
    "offers": {
      "@type": "AggregateOffer",
      "lowPrice": "0",
      "highPrice": "200",
      "priceCurrency": "EUR",
      "offerCount": "4"
    }
  };

  const features = [
    { icon: Layers, title: t('feat_1_title'), desc: t('feat_1_desc'), color: 'bg-blue-50 text-brand-blue border border-blue-200' },
    { icon: BrainCircuit, title: t('feat_2_title'), desc: t('feat_2_desc'), color: 'bg-orange-50 text-brand-orange border border-orange-200' },
    { icon: BadgeCheck, title: t('feat_3_title'), desc: t('feat_3_desc'), color: 'bg-blue-50 text-brand-blue border border-blue-200' },
    { icon: Target, title: t('feat_4_title'), desc: t('feat_4_desc'), color: 'bg-orange-50 text-brand-orange border border-orange-200' },
  ];

  const audiences = [
    { icon: GraduationCap, label: t('target_students'), color: 'border-blue-200 bg-white', iconColor: 'text-brand-blue', accent: 'bg-blue-50' },
    { icon: Briefcase, label: t('target_pros'), color: 'border-orange-200 bg-white', iconColor: 'text-brand-orange', accent: 'bg-orange-50' },
    { icon: Star, label: t('target_leaders'), color: 'border-gray-300 bg-white', iconColor: 'text-brand-dark', accent: 'bg-gray-100' },
    { icon: Sparkles, label: t('target_beginners'), color: 'border-blue-300 bg-white', iconColor: 'text-brand-blue', accent: 'bg-blue-50' },
  ];

  const levelMap = { beginner: t('beginner'), intermediate: t('intermediate'), advanced: t('advanced') };

  return (
    <div className="min-h-screen" data-testid="landing-page">
      <SEO
        path="/"
        description={language === 'fr'
          ? "La formation IA la plus complète en français. 4 programmes, 35 modules, assistant IA Claude intégré, quiz adaptatifs, certificats. Du débutant à expert en 30 min/jour."
          : "The most comprehensive AI training. 4 programs, 35 modules, integrated AI assistant, adaptive quizzes, certificates. From beginner to expert in 30 min/day."}
        jsonLd={organizationJsonLd}
      />
      <section className="relative overflow-hidden bg-brand-dark" data-testid="hero-section">
        <div className="absolute inset-0 opacity-5" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23007BFF' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
        }} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-24 relative">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <Badge className="mb-6 bg-brand-blue/10 text-brand-blue border-brand-blue/30 hover:bg-brand-blue/20 px-3 py-1" data-testid="hero-badge">
                <Sparkles className="w-3.5 h-3.5 mr-1.5" />
                {language === 'fr' ? 'Formation IA 2026' : 'AI Training 2026'}
              </Badge>
              <Badge className="mb-6 ml-2 bg-emerald-500/10 text-emerald-400 border-emerald-500/30 hover:bg-emerald-500/20 px-3 py-1" data-testid="ai-act-badge">
                <ShieldCheck className="w-3.5 h-3.5 mr-1.5" />
                {language === 'fr' ? 'Conforme AI Act 2025 & 2026' : 'AI Act 2025 & 2026 Compliant'}
              </Badge>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-white leading-[1.1]" style={{ fontFamily: 'Poppins' }} data-testid="hero-title">
                {t('hero_title')}
              </h1>
              <p className="mt-6 text-base md:text-lg text-gray-300 leading-relaxed max-w-xl" data-testid="hero-subtitle">
                {t('hero_subtitle')}
              </p>
              <div className="mt-8 flex flex-wrap items-center gap-4">
                <Button
                  size="lg"
                  className="bg-brand-orange hover:brightness-110 text-white shadow-lg shadow-brand-orange/25 active:scale-95 transition-all font-semibold"
                  onClick={() => navigate('/auth?mode=register')}
                  data-testid="hero-cta-btn"
                >
                  {t('hero_cta')}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  className="border-white/20 text-white hover:bg-white/10 backdrop-blur"
                  onClick={() => navigate('/programs')}
                  data-testid="hero-explore-btn"
                >
                  {t('hero_explore')}
                </Button>
                <OwlMascot size={64} className="hidden sm:block ml-2 animate-bounce-slow" />
              </div>
              {/* Stats */}
              <div className="mt-12 flex gap-8">
                {[
                  { value: '4', label: language === 'fr' ? 'Programmes' : 'Programs' },
                  { value: '35', label: language === 'fr' ? 'Modules' : 'Modules' },
                  { value: '15+', label: language === 'fr' ? 'Outils IA' : 'AI Tools' },
                ].map((stat, i) => (
                  <div key={i} className="text-left">
                    <p className="text-2xl font-bold text-brand-orange" style={{ fontFamily: 'Poppins' }}>{stat.value}</p>
                    <p className="text-sm text-gray-400">{stat.label}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="hidden lg:block relative">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl shadow-black/30 ring-1 ring-white/10">
                <img
                  src="https://customer-assets.emergentagent.com/job_ai-bootcamp-22/artifacts/rm4nun9q_Capture%20d%E2%80%99e%CC%81cran%202026-03-13%20a%CC%80%2013.06.54.png"
                  alt="AI Training"
                  className="w-full h-[420px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-brand-dark/60 to-transparent" />
              </div>
              {/* Floating card */}
              <div className="absolute -bottom-6 -left-6 bg-white rounded-xl shadow-xl p-4 border border-gray-100">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#007BFF15' }}>
                    <Award className="w-5 h-5 text-brand-blue" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-brand-dark">{language === 'fr' ? 'Certification' : 'Certification'}</p>
                    <p className="text-xs text-gray-500">{language === 'fr' ? 'À chaque niveau' : 'At every level'}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-brand-light" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-left max-w-2xl">
            <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-brand-dark" style={{ fontFamily: 'Poppins' }}>
              {t('features_title')}
            </h2>
          </div>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feat, i) => (
              <div
                key={i}
                className={`bg-white rounded-xl border border-gray-200 p-6 hover:shadow-md transition-shadow animate-fade-in-up stagger-${i + 1}`}
                data-testid={`feature-card-${i}`}
              >
                <div className={`w-11 h-11 rounded-lg flex items-center justify-center ${feat.color} mb-4`}>
                  <feat.icon className="w-5 h-5" />
                </div>
                <h3 className="text-base font-semibold text-brand-dark mb-2">{feat.title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed">{feat.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Programs Preview */}
      <section className="py-20 bg-brand-light" data-testid="programs-preview-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between mb-12">
            <div>
              <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-brand-dark" style={{ fontFamily: 'Poppins' }}>
                {t('programs_preview')}
              </h2>
            </div>
            <Button variant="ghost" className="hidden sm:flex text-brand-blue hover:text-brand-blue/80" onClick={() => navigate('/programs')} data-testid="view-all-programs">
              {language === 'fr' ? 'Voir tout' : 'View all'}
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {programs.map((prog, i) => (
              <div
                key={prog.program_id}
                className="group bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-lg hover:border-gray-300 transition-shadow cursor-pointer"
                onClick={() => navigate(`/programs/${prog.program_id}`)}
                data-testid={`program-card-${prog.program_id}`}
              >
                <div className="relative h-40 overflow-hidden">
                  <img src={prog.image} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  <div className="absolute top-3 left-3">
                    <Badge className="text-white border-0 text-xs" style={{ backgroundColor: prog.color }}>
                      {prog.duration}
                    </Badge>
                  </div>
                </div>
                <div className="p-5">
                  <h3 className="font-semibold text-brand-dark mb-1.5" style={{ fontFamily: 'Poppins' }}>
                    {language === 'fr' ? prog.title_fr : prog.title_en}
                  </h3>
                  <p className="text-sm text-slate-500 line-clamp-2 mb-3">
                    {language === 'fr' ? prog.description_fr : prog.description_en}
                  </p>
                  <div className="flex items-center gap-3 text-xs text-slate-400">
                    <span className="flex items-center gap-1">
                      <BookOpen className="w-3.5 h-3.5" /> {prog.modules_count} {t('modules')}
                    </span>
                    <span>{levelMap[prog.level] || prog.level}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Audiences Section */}
      <section className="py-20 bg-brand-light" data-testid="audiences-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-brand-dark mb-12" style={{ fontFamily: 'Poppins' }}>
            {t('target_title')}
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {audiences.map((aud, i) => (
              <div key={i} className={`rounded-xl border p-6 ${aud.color} hover:shadow-md transition-shadow`} data-testid={`audience-card-${i}`}>
                <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${aud.accent} mb-4`}>
                  <aud.icon className={`w-6 h-6 ${aud.iconColor}`} />
                </div>
                <p className="font-semibold text-brand-dark">{aud.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Carousel */}
      <section className="py-20" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-slate-900 mb-3 text-center" style={{ fontFamily: 'Poppins' }}>
            {language === 'fr' ? 'Ils nous font confiance' : 'They trust us'}
          </h2>
          <p className="text-slate-500 text-center mb-12">{language === 'fr' ? 'Découvrez les retours de nos apprenants' : 'See feedback from our learners'}</p>
          
          {(() => {
            const testimonials = language === 'fr' ? [
              { name: 'Lucas M.', role: 'Lycéen, Terminale', avatar: 'LM', quote: 'J\'ai compris en 2h ce que mes profs n\'ont jamais pu m\'expliquer sur l\'IA. Les quiz m\'ont aidé à valider mes acquis pour Parcoursup.', color: 'bg-brand-blue' },
              { name: 'Sarah K.', role: 'Chef de projet digital', avatar: 'SK', quote: 'Le Playground IA m\'a permis de tester des prompts concrets pour mon travail. J\'ai gagné 2h par jour sur mes tâches répétitives.', color: 'bg-brand-orange' },
              { name: 'Philippe D.', role: 'DG, PME industrielle', avatar: 'PD', quote: 'Le module Stratégie IA m\'a donné les arguments pour convaincre mon COMEX. On a lancé notre premier projet IA en 3 mois.', color: 'bg-brand-blue' },
              { name: 'Amina R.', role: 'En reconversion', avatar: 'AR', quote: 'Débutante totale, j\'ai pu suivre sans difficulté. Le parcours personnalisé m\'a guidé vers le bon programme. Très bien fait !', color: 'bg-brand-orange' },
            ] : [
              { name: 'Lucas M.', role: 'High school student', avatar: 'LM', quote: 'I understood in 2 hours what my teachers could never explain about AI. The quizzes helped me validate my knowledge.', color: 'bg-brand-blue' },
              { name: 'Sarah K.', role: 'Digital project manager', avatar: 'SK', quote: 'The AI Playground let me test concrete prompts for my work. I save 2 hours a day on repetitive tasks.', color: 'bg-brand-orange' },
              { name: 'Philippe D.', role: 'CEO, Industrial SME', avatar: 'PD', quote: 'The AI Strategy module gave me the arguments to convince my board. We launched our first AI project in 3 months.', color: 'bg-brand-blue' },
              { name: 'Amina R.', role: 'Career changer', avatar: 'AR', quote: 'Complete beginner, I was able to follow easily. The personalized path guided me to the right program. Very well done!', color: 'bg-brand-orange' },
            ];
            const current = testimonials[currentTestimonial];
            
            return (
              <div className="relative max-w-2xl mx-auto">
                {/* Main testimonial card */}
                <div className="bg-white rounded-2xl border border-slate-200 p-8 md:p-10 shadow-sm transition-all duration-500" data-testid={`testimonial-${currentTestimonial}`}>
                  <div className="flex flex-col items-center text-center">
                    <div className={`w-16 h-16 rounded-full flex items-center justify-center text-white text-xl font-bold ${current.color} mb-5 shadow-lg`}>
                      {current.avatar}
                    </div>
                    <p className="text-lg text-slate-700 leading-relaxed italic mb-6">"{current.quote}"</p>
                    <div className="flex gap-0.5 mb-4">
                      {[1,2,3,4,5].map(s => <Star key={s} className="w-4 h-4 fill-brand-orange text-brand-orange" />)}
                    </div>
                    <p className="font-semibold text-brand-dark">{current.name}</p>
                    <p className="text-sm text-slate-500">{current.role}</p>
                  </div>
                </div>

                {/* Navigation arrows */}
                <button
                  onClick={() => setCurrentTestimonial(prev => (prev - 1 + 4) % 4)}
                  className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 md:-translate-x-12 w-10 h-10 rounded-full bg-white border border-slate-200 shadow-md flex items-center justify-center hover:bg-slate-50 transition-colors"
                  data-testid="testimonial-prev"
                >
                  <ChevronLeft className="w-5 h-5 text-slate-600" />
                </button>
                <button
                  onClick={() => setCurrentTestimonial(prev => (prev + 1) % 4)}
                  className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 md:translate-x-12 w-10 h-10 rounded-full bg-white border border-slate-200 shadow-md flex items-center justify-center hover:bg-slate-50 transition-colors"
                  data-testid="testimonial-next"
                >
                  <ChevronRight className="w-5 h-5 text-slate-600" />
                </button>

                {/* Dots indicator */}
                <div className="flex justify-center gap-2 mt-6">
                  {testimonials.map((_, i) => (
                    <button
                      key={i}
                      onClick={() => setCurrentTestimonial(i)}
                      className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${
                        i === currentTestimonial ? 'bg-brand-orange w-7' : 'bg-gray-300 hover:bg-gray-400'
                      }`}
                      data-testid={`testimonial-dot-${i}`}
                    />
                  ))}
                </div>
              </div>
            );
          })()}
        </div>
      </section>

      {/* Social Proof / Advantages */}
      <section className="py-16 bg-brand-dark" data-testid="social-proof-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-white" style={{ fontFamily: 'Poppins' }}>
              {language === 'fr' ? 'IA Academy en chiffres' : 'IA Academy in numbers'}
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-6 text-center">
            {[
              { value: '35', label: language === 'fr' ? 'Modules de formation' : 'Training modules' },
              { value: '9', label: language === 'fr' ? 'Quiz interactifs' : 'Interactive quizzes' },
              { value: '15+', label: language === 'fr' ? 'Outils IA couverts' : 'AI tools covered' },
              { value: '2', label: language === 'fr' ? 'Langues (FR/EN)' : 'Languages (FR/EN)' },
              { value: '4', label: language === 'fr' ? 'Niveaux de formation' : 'Training levels' },
            ].map((stat, i) => (
              <div key={i} className="p-4">
                <p className="text-4xl font-bold text-brand-orange mb-1" style={{ fontFamily: 'Poppins' }}>{stat.value}</p>
                <p className="text-sm text-gray-400">{stat.label}</p>
              </div>
            ))}
          </div>
          <div className="mt-10 flex flex-wrap justify-center gap-4 text-sm text-gray-400">
            {(language === 'fr'
              ? ['100% IA — pas de contenu générique', 'Assistant IA intégré (exclusivité)', 'De 0€ à expert en 8h', 'Certificats partageables']
              : ['100% AI — no generic content', 'Built-in AI assistant (exclusive)', 'From €0 to expert in 8h', 'Shareable certificates']
            ).map((item, i) => (
              <span key={i} className="flex items-center gap-1.5 bg-white/5 border border-white/10 rounded-full px-4 py-1.5">
                <Check className="w-3.5 h-3.5 text-brand-blue" /> {item}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Footer */}
      <section className="py-16 bg-brand-blue" data-testid="cta-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-4" style={{ fontFamily: 'Poppins' }}>
            {language === 'fr' ? 'Prêt à prendre de l\'avance sur l\'IA ?' : 'Ready to get ahead in AI?'}
          </h2>
          <p className="text-blue-100 mb-8 max-w-lg mx-auto">
            {language === 'fr' ? 'Commencez gratuitement. Montez en compétences. Obtenez votre certificat.' : 'Start for free. Build your skills. Get your certificate.'}
          </p>
          <div className="flex items-center justify-center gap-3 flex-wrap">
            <Button
              size="lg"
              className="bg-brand-orange text-white hover:brightness-110 shadow-lg active:scale-95 transition-all font-semibold"
              onClick={() => navigate('/auth?mode=register')}
              data-testid="cta-register-btn"
            >
              {language === 'fr' ? 'Commencer gratuitement' : 'Start for Free'}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="border-white/30 text-white hover:bg-white/10"
              onClick={() => navigate('/pricing')}
              data-testid="cta-pricing-btn"
            >
              {language === 'fr' ? 'Voir les tarifs' : 'View Pricing'}
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 border-t border-gray-200 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <img src="https://customer-assets.emergentagent.com/job_2071b760-1de1-4ab9-81d8-8ca2ec59ffcc/artifacts/lq8u7cy5_LOGO%20IA.png" alt="IA Academy" className="w-14 h-14 rounded-xl" data-testid="footer-logo" />
              <div>
                <span className="font-bold text-brand-dark text-sm" style={{ fontFamily: 'Poppins' }}>IA Academy</span>
                <span className="text-xs text-gray-400 ml-2">
                  {language === 'fr' ? 'Progressez. 30 min/jour. Résultats concrets.' : 'Level Up. 30 Min a Day. Real Results.'}
                </span>
              </div>
            </div>
            <div className="flex items-center gap-4 text-sm text-gray-400">
              <a href="/legal" className="hover:text-gray-600 transition-colors" data-testid="footer-legal-link">
                {language === 'fr' ? 'Mentions légales' : 'Legal'}
              </a>
              <span>&middot;</span>
              <p>&copy; 2026 IA Academy. {language === 'fr' ? 'Tous droits réservés.' : 'All rights reserved.'}</p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
